import { PlatformEnum } from '@vacationtracker/shared/data/platforms'

import { getRememberedLogin } from '../../../util/remembered-login'
import { authInfoToOutcome, isDetectedPlatform, stepAndBannerFromLookup } from './outcomes'
import { ISigninQueryParams } from './query-params'
import { ISigninAccountHandover, IStepAndBanner, SigninBanner, SigninStep } from './types'

/**
 * Query params win over the remembered marker, so a support or marketing link always
 * lands where it says it does.
 *
 * A remembered password account opens straight on the password step, with the username the
 * last lookup stored. That saves a click and a request: resolving it again on page load
 * would cost a lookup per visit to learn something already known.
 */
/**
 * What the page has to say before anyone has done anything. Derived with the step rather than
 * raised from an effect: the app sends visitors here with the reason in the query string, and
 * an effect would announce it a paint after the form was already on screen.
 */
export const resolveInitialBanner = (queryParams: ISigninQueryParams): SigninBanner =>
  queryParams.error === 'subscription_expired' ? { kind: 'subscriptionExpired' } : { kind: 'none' }

/** The steps a handover can open on: the ones that offer an account to sign in to. */
const HANDOVER_STEPS: SigninStep['name'][] = ['choosePlatform', 'platform', 'password']

/** Router state is whatever the last navigation left there, so the shape is checked. */
const readHandover = (state: unknown): ISigninAccountHandover | null => {
  const handed = (state as { signinAccount?: unknown } | null | undefined)?.signinAccount
  if (typeof handed !== 'object' || handed === null) return null

  const { email, authInfo, inactive } = handed as Partial<ISigninAccountHandover>
  if (typeof email !== 'string' || !email) return null
  if (inactive === true) return { email, inactive }
  if (typeof authInfo?.platform !== 'string') return null

  return { email, authInfo }
}

/**
 * Where sign-up's answer lands. It is read as the lookup this page would have made, so an
 * address with several accounts opens on the choice between them and one with a single
 * account opens on that one, with no second request and nothing for the visitor to retype.
 */
export const resolveHandover = (state: unknown): IStepAndBanner | null => {
  const handover = readHandover(state)
  if (!handover) return null

  // A deactivated account is not one to offer: signing up again cannot fix it and neither can
  // this page, so it opens on the address with the one thing left to do.
  if (handover.inactive) {
    return {
      step: { name: 'email', email: handover.email },
      banner: { kind: 'accessNeeded', email: handover.email },
    }
  }

  const { step } = stepAndBannerFromLookup({
    outcome: authInfoToOutcome(handover.authInfo),
    email: handover.email,
    currentStep: { name: 'email', email: handover.email },
  })
  // Any other step means the answer does not name an account to sign in to after all, which
  // is not something to announce as one.
  if (!HANDOVER_STEPS.includes(step.name)) return null

  return { step, banner: { kind: 'companyAlreadySignedUp' } }
}

export const resolveInitialStep = (queryParams: ISigninQueryParams): SigninStep => {
  if (queryParams.adpLogin) {
    return { name: 'adp', email: '' }
  }

  // The reset used to open as a modal over whichever step the page had resolved. As a step of
  // its own it replaces that resolution, or the link would land on a password form.
  if (queryParams.forgotpassword) {
    return { name: 'resetRequest', email: getRememberedLogin()?.email ?? '' }
  }

  if (isDetectedPlatform(queryParams.platform)) {
    return { name: 'platform', platform: queryParams.platform, source: 'query' }
  }
  if (queryParams.platform === String(PlatformEnum.Email)) {
    return { name: 'email', email: '' }
  }

  const remembered = getRememberedLogin()
  if (remembered) {
    if (isDetectedPlatform(remembered.platform)) {
      return {
        name: 'platform',
        platform: remembered.platform,
        email: remembered.email,
        source: 'remembered',
      }
    }
    if (remembered.platform === PlatformEnum.Email) {
      // Signing in needs the username, so the password step is only reachable once a lookup
      // has put one in the marker. Without it the address is all there is to go on.
      if (remembered.username) {
        return {
          name: 'password',
          email: remembered.email,
          username: remembered.username,
          adpEnabled: Boolean(remembered.adpEnabled),
        }
      }
      return { name: 'email', email: remembered.email }
    }
  }

  return { name: 'email', email: '' }
}
